import type { DisplayItem } from '../shared';

export interface PlayerState {
  currentId: string | null;
  failedIds: string[];
  cycle: number;
}

export const initialPlayerState: PlayerState = {
  currentId: null,
  failedIds: [],
  cycle: 0
};

export function currentItem(state: PlayerState, items: DisplayItem[]): DisplayItem | undefined {
  return items.find((item) => item.id === state.currentId) ?? items.find((item) => !state.failedIds.includes(item.id));
}

export function reconcilePlayer(state: PlayerState, items: DisplayItem[]): PlayerState {
  if (items.length === 0) return { currentId: null, failedIds: [], cycle: state.cycle };
  const availableIds = new Set(items.map((item) => item.id));
  const failedIds = state.failedIds.filter((id) => availableIds.has(id));
  const currentStillAvailable = state.currentId && availableIds.has(state.currentId) && !failedIds.includes(state.currentId);
  return {
    ...state,
    failedIds,
    currentId: currentStillAvailable ? state.currentId : (items.find((item) => !failedIds.includes(item.id))?.id ?? null)
  };
}

export function advancePlayer(state: PlayerState, items: DisplayItem[], failedCurrent = false): PlayerState {
  if (items.length === 0) return { currentId: null, failedIds: [], cycle: state.cycle };
  let failedIds = failedCurrent && state.currentId && !state.failedIds.includes(state.currentId)
    ? [...state.failedIds, state.currentId]
    : state.failedIds;
  let playable = items.filter((item) => !failedIds.includes(item.id));
  if (playable.length === 0) {
    failedIds = [];
    playable = items;
  }
  const currentIndex = playable.findIndex((item) => item.id === state.currentId);
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % playable.length;
  const wrapped = currentIndex >= 0 && nextIndex === 0;
  return {
    currentId: playable[nextIndex]?.id ?? null,
    failedIds,
    cycle: state.cycle + (wrapped ? 1 : 0)
  };
}

export function upcomingItem(state: PlayerState, items: DisplayItem[]): DisplayItem | undefined {
  if (items.length < 2) return undefined;
  const playable = items.filter((item) => !state.failedIds.includes(item.id));
  const index = playable.findIndex((item) => item.id === state.currentId);
  return playable[index < 0 ? 0 : (index + 1) % playable.length];
}
